import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, Table, Badge, Button, Input, FormGroup } from 'reactstrap';
import moment from 'moment';
import './SubDetailsSidebar.css';
import notFoundImg from '../../../static/img/not-found.png';

const AdminActivitySidebar = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { creditHistory, loading } = useSelector(state => ({
    creditHistory: state.Admin.creditHistory || { transactions: [], stats: {}, pagination: {} },
    loading: state.Admin.creditHistoryLoading
  }));

  useEffect(() => {
    if (isOpen) {
      fetchCreditHistory();
    }
  }, [isOpen, currentPage, dateFilter]);

  const fetchCreditHistory = () => {
    let startDate = null;
    let endDate = new Date();

    switch (dateFilter) {
      case 'today':
        startDate = moment().startOf('day').toDate();
        break;
      case 'week':
        startDate = moment().subtract(7, 'days').toDate();
        break;
      case 'month':
        startDate = moment().subtract(30, 'days').toDate();
        break;
      case 'year':
        startDate = moment().subtract(1, 'year').toDate();
        break;
      default:
        startDate = null;
    }

    dispatch({
      type: 'GET_CREDIT_HISTORY',
      payload: {
        page: currentPage,
        limit: 50,
        startDate: startDate,
        endDate: endDate,
        search: searchTerm
      }
    });
  };

  const formatDate = (date) => {
    return {
      date: moment(date).format('MMM DD, YYYY'),
      time: moment(date).format('h:mm A')
    };
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const { transactions = [], stats = {}, pagination = {} } = creditHistory;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName='right right-without-bg'
      backdrop={false}
      className='main-modal right right-side-modal style-three'
    >
      <ModalHeader toggle={onClose}>
        <h3>Admin Credit History</h3>
        <p className="text-muted mb-0">
          Credit transactions and plan change activity
        </p>
      </ModalHeader>
      <ModalBody>
        <div className='filters-modal-content'>
          {/* Stats Cards */}
          <div className='function-box'>
            <div className="user-info mb-3">
              <div className="user-title-section">
                <span title="Credits Added Today">
                  <i className="icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle opacity="0.1" cx="16" cy="16" r="16" fill="#9333EA"></circle>
                      <path d="M16 22C12.6863 22 10 19.3137 10 16C10 12.6863 12.6863 10 16 10C19.3137 10 22 12.6863 22 16C22 19.3137 19.3137 22 16 22ZM16 20C18.2091 20 20 18.2091 20 16C20 13.7909 18.2091 12 16 12C13.7909 12 12 13.7909 12 16C12 18.2091 13.7909 20 16 20ZM15 13H17V15H19V17H17V19H15V17H13V15H15V13Z" fill="#9333EA"></path>
                    </svg>
                  </i>
                  Credits Added Today
                </span>
                <h3 className="user-name" style={{ color: '#9333ea' }}>{formatCurrency(stats.todayTotal || 0)}</h3>
                <small>{stats.todayCount || 0} transactions</small>
              </div>
            </div>

            <div className="user-info mb-3">
              <div className="user-title-section">
                <span title="Credits This Month">
                  <i className="icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle opacity="0.1" cx="16" cy="16" r="16" fill="#34C759"></circle>
                      <path d="M16 22C12.6863 22 10 19.3137 10 16C10 12.6863 12.6863 10 16 10C19.3137 10 22 12.6863 22 16C22 19.3137 19.3137 22 16 22ZM16 20C18.2091 20 20 18.2091 20 16C20 13.7909 18.2091 12 16 12C13.7909 12 12 13.7909 12 16C12 18.2091 13.7909 20 16 20ZM15 13H17V16H20V18H15V13Z" fill="#34C759"></path>
                    </svg>
                  </i>
                  This Month
                </span>
                <h3 className="user-name text-success">{formatCurrency(stats.monthTotal || 0)}</h3>
                <small>{stats.monthCount || 0} transactions</small>
              </div>
            </div>

            <div className="user-info mb-3">
              <div className="user-title-section">
                <span title="All Time Total">
                  <i className="icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle opacity="0.1" cx="16" cy="16" r="16" fill="#EF4D56"></circle>
                      <path d="M16 22C12.6863 22 10 19.3137 10 16C10 12.6863 12.6863 10 16 10C19.3137 10 22 12.6863 22 16C22 19.3137 19.3137 22 16 22ZM16 20C18.2091 20 20 18.2091 20 16C20 13.7909 18.2091 12 16 12C13.7909 12 12 13.7909 12 16C12 18.2091 13.7909 20 16 20Z" fill="#EF4D56"></path>
                      <path d="M15 11H17V17H15V11Z" fill="#EF4D56"></path>
                      <path d="M15 18H17V20H15V18Z" fill="#EF4D56"></path>
                    </svg>
                  </i>
                  All Time Total
                </span>
                <h3 className="user-name" style={{ color: '#EF4D56' }}>{formatCurrency(stats.allTimeTotal || 0)}</h3>
                <small>{stats.allTimeCount || 0} transactions</small>
              </div>
            </div>

            <div className="user-info mb-3">
              <div className="user-title-section">
                <span title="Active Admins">
                  <i className="icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle opacity="0.1" cx="16" cy="16" r="16" fill="#007AFF"></circle>
                      <path d="M16 11C14.3431 11 13 12.3431 13 14C13 15.6569 14.3431 17 16 17C17.6569 17 19 15.6569 19 14C19 12.3431 17.6569 11 16 11ZM16 15C15.4477 15 15 14.5523 15 14C15 13.4477 15.4477 13 16 13C16.5523 13 17 13.4477 17 14C17 14.5523 16.5523 15 16 15ZM12 21V20C12 18.8954 12.8954 18 14 18H18C19.1046 18 20 18.8954 20 20V21H12Z" fill="#007AFF"></path>
                    </svg>
                  </i>
                  Active Admins
                </span>
                <h3 className="user-name" style={{ color: '#007AFF' }}>{stats.activeAdmins || 0}</h3>
                <small>admins adding credits</small>
              </div>
            </div>
          </div>

          {/* Filters and Table */}
          <div className='function-box workspace-area-detail mt-3'>
            <div className="sidebar-body">
              <div className='tabs-area'>
                {/* Filters Section */}
                <div className="mb-3 px-3">
                  <FormGroup className="mb-2">
                    <Input
                      type="text"
                      placeholder="Search by admin name, workspace, or reason..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setCurrentPage(1);
                          fetchCreditHistory();
                        }
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      type="select"
                      value={dateFilter}
                      onChange={(e) => {
                        setDateFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last 30 Days</option>
                      <option value="year">Last Year</option>
                    </Input>
                  </FormGroup>
                </div>

                {/* Activity Table */}
                <div className="table-container filters-table">
                  {loading ? (
                    <div className='notfound-area'>
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className='notfound-area'>
                      <div className='img-box'>
                        <img alt="..." src={notFoundImg} />
                      </div>
                      <p>No credit transactions found</p>
                    </div>
                  ) : (
                    <>
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Date & Time</th>
                            <th>Admin</th>
                            <th>Activity Type</th>
                            <th>Workspace</th>
                            <th>Details</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((transaction) => {
                            const dateTime = formatDate(transaction.date);
                            return (
                            <tr key={transaction.id}>
                              <td style={{ minWidth: '110px' }}>
                                <div style={{ whiteSpace: 'nowrap' }}>
                                  <div><small className="text-muted">{dateTime.date}</small></div>
                                  <div><small className="text-muted">{dateTime.time}</small></div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <strong>{transaction.adminName}</strong>
                                  <br />
                                  <small className="text-muted">{transaction.adminEmail}</small>
                                </div>
                              </td>
                              <td>
                                {transaction.type === 'admin_credit' ? (
                                  <Badge color="success" className="px-3 py-1">
                                    + Credit Addition
                                  </Badge>
                                ) : (
                                  <Badge color="info" className="px-3 py-1">
                                    <i className="fa fa-exchange-alt mr-1"></i>
                                    Plan Change
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div>
                                  <strong>{transaction.workspaceName}</strong>
                                  <br />
                                  <small className="text-muted">
                                    User: {transaction.userName}
                                    {transaction.userEmail && ` (${transaction.userEmail})`}
                                  </small>
                                </div>
                              </td>
                              <td>
                                {transaction.type === 'admin_credit' ? (
                                  <div>
                                    <Badge color="success" className="mb-1">
                                      +{formatCurrency(transaction.amount)} Credits
                                    </Badge>
                                    <br />
                                    <small className="text-muted">
                                      Balance: {formatCurrency(transaction.previousBalance)} → {formatCurrency(transaction.newBalance)}
                                    </small>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="mb-1">
                                      <strong>From:</strong> {transaction.oldPlanName || 'No Plan'}
                                      <br />
                                      <strong>To:</strong> {transaction.newPlanName}
                                    </div>
                                    <small className="text-muted">
                                      Price: {formatCurrency(transaction.oldPlanPrice || 0)} → {formatCurrency(transaction.newPlanPrice || 0)}
                                    </small>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className="text-dark">{transaction.reason}</span>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      {/* Pagination */}
                      {pagination.pages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                          <div className="text-muted">
                            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, pagination.total)} of {pagination.total} transactions
                          </div>
                          <div>
                            <Button
                              size="sm"
                              color="outline-primary"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                              className="mr-2"
                            >
                              <i className="fa fa-chevron-left"></i> Previous
                            </Button>
                            <span className="mx-3">
                              Page <strong>{currentPage}</strong> of <strong>{pagination.pages}</strong>
                            </span>
                            <Button
                              size="sm"
                              color="outline-primary"
                              disabled={currentPage >= pagination.pages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                              className="ms-2"
                            >
                              Next <i className="fa fa-chevron-right"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AdminActivitySidebar;
